import React, { useEffect, useState } from 'react';
import { 
  Box, 
  Button, 
  Menu, 
  MenuItem, 
  Chip,
  IconButton,
  Typography
} from '@mui/material';
import FlagIcon from '@mui/icons-material/Flag';
import CloseIcon from '@mui/icons-material/Close';
import { authFetch } from '../utils/authFetch';
import PropTypes from 'prop-types';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function LapList({ laps, selectedLaps, setSelectedLaps }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [lapList, setLapList] = useState(laps);
  const open = Boolean(anchorEl);

  // Sync lapList with laps prop
  useEffect(() => {
    setLapList(laps);
  }, [laps]);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
    // Always fetch latest laps when opening the menu
    authFetch(`${BACKEND_URL}/laps/`)
      .then(res => {
        if (res && res.ok) return res.json();
        return null;
      })
      .then(data => {
        if (data) setLapList([...data].sort());
      });
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLapSelect = (lapId) => {
    if (!selectedLaps.includes(lapId)) {
      setSelectedLaps([...selectedLaps, lapId]);
    }
    handleClose();
  };

  const handleLapRemove = (lapId) => {
    setSelectedLaps(selectedLaps.filter(id => id !== lapId));
  };

  // Format lap time from filename (format: lap_mm-ss-ms.json)
  const formatLapTime = (lapId) => {
    const match = lapId.match(/lap_(\d+)-(\d+)-(\d+)\.json/);
    if (match) {
      return `${match[1]}:${match[2]}.${match[3]}`;
    }
    return lapId;
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
      <Button
        variant="outlined"
        onClick={handleClick}
        startIcon={<FlagIcon />}
        aria-label="Select lap"
        sx={{
          minWidth: '200px',
          maxWidth: '200px',
          justifyContent: 'flex-start',
          borderColor: 'primary.main',
          height: 40,
          fontSize: '1rem',
        }}
      >
        Select Lap
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            backgroundColor: 'background.paper',
            maxHeight: 300,
            width: '200px'
          }
        }}
      >
        {lapList.map((lap) => (
          <MenuItem 
            key={lap} 
            onClick={() => handleLapSelect(lap)}
            disabled={selectedLaps.includes(lap)}
            aria-label={`Select lap ${lap}`}
            sx={{
              '&:hover': {
                backgroundColor: 'rgba(0, 210, 190, 0.08)'
              },
              '&.Mui-disabled': {
                opacity: 0.5
              }
            }}
          >
            {formatLapTime(lap)}
          </MenuItem>
        ))}
      </Menu>

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {selectedLaps.map((lap) => (
          <Chip
            key={lap}
            label={formatLapTime(lap)}
            onDelete={() => handleLapRemove(lap)}
            deleteIcon={<CloseIcon />}
            aria-label={`Remove lap ${lap}`}
            sx={{
              backgroundColor: 'rgba(0, 168, 107, 0.2)',
              border: '1px solid rgba(0, 168, 107, 0.5)',
              '& .MuiChip-label': {
                color: '#00A86B',
                fontWeight: 500
              },
              '& .MuiChip-deleteIcon': {
                color: '#00A86B',
                '&:hover': {
                  color: '#FF1E1E'
                }
              }
            }}
          />
        ))}
      </Box>
    </Box>
  );
}

LapList.propTypes = {
  laps: PropTypes.array.isRequired,
  selectedLaps: PropTypes.array.isRequired,
  setSelectedLaps: PropTypes.func.isRequired
};

export default LapList;
